import { Component, DestroyRef, inject, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { SelectComponent } from '@controls/select/select.component';
import { CompanyRecord } from '@models/companies/company-record';
import { User } from '@models/organisations/user';
import { UserRecord } from '@models/organisations/user-record';
import { PartnerAndManagerChangeRequest } from '@models/relationships/partner-and-manager-change-request';
import { SelectOption } from '@models/ui-models/select-options';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import {
  CommonModalWrapperComponent
} from '@shared/components/commons/common-modal-wrapper/common-modal-wrapper.component';
import { CompaniesApiService } from "@shared/services/companies-api.service";
import { UsersApiService } from '@shared/services/users-api.service';
import { ToastrService } from 'ngx-toastr';
import { catchError, EMPTY } from 'rxjs';

export interface PartnerAndAccountManagerFormControls {
  partnerManagerId: FormControl<string | null>;
  accountManagerId: FormControl<string | null>;
}

export type PartnerAndAccountManagerFormGroup = FormGroup<PartnerAndAccountManagerFormControls>;

@Component({
  selector: 'app-company-form-partner-account',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    CommonModalWrapperComponent,
    SelectComponent,
  ],
  providers: [],
  templateUrl: './company-form-partner-account.component.html',
  styleUrl: './company-form-partner-account.component.scss',
})
export class CompanyFormPartnerAccount implements OnInit {
  private companiesApiService = inject(CompaniesApiService);
  private usersService = inject(UsersApiService);
  private toastr = inject(ToastrService);
  private destroyRef = inject(DestroyRef);
  private activeModal = inject(NgbActiveModal);

  @Input() companies!: CompanyRecord[];

  users: UserRecord[] = [];
  usersOption: SelectOption[] = [];
  isLoading = false;
  form: PartnerAndAccountManagerFormGroup = new FormGroup({
    partnerManagerId: new FormControl<string | null>(null),
    accountManagerId: new FormControl<string | null>(null),
  });

  ngOnInit(): void {
    this.usersService.getAssignableUsersCached()
      .subscribe({
        next: (users) => {
          this.users = users;
          this.usersOption = [
            { value: null, label: '' },
            ...users.map(user => ({ value: user.id, label: user.fullName }))
          ];
          this.setupChange();
        }
      });
  }

  setupChange(): void {
    if(this.companies.length === 1) {
      setTimeout(() => {
        this.form.patchValue({
          partnerManagerId: this.companies[0].partnerManagerId,
          accountManagerId: this.companies[0].accountManagerId,
        });
      });
    }
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.isLoading = true;
    this.form.disable();

    var request = new PartnerAndManagerChangeRequest({
        partnerManagerId: this.form.controls.partnerManagerId.value,
        accountManagerId: this.form.controls.accountManagerId.value,
        companyIds: this.companies.map(x => x.entityId)
    })
    this.companiesApiService.changePartnerAndAccountManager(request)
        .pipe(
          catchError((error) => {
            console.error(error);
            this.form.enable();
            this.isLoading = false;
            this.toastr.error('Error happened while applying updates', 'Error');
            return EMPTY;
          }),
          takeUntilDestroyed(this.destroyRef)
        ).subscribe(res => {
          for (const company of this.companies) {
            company.partnerManagerId = this.form.controls.partnerManagerId.value;
            company.accountManagerId = this.form.controls.accountManagerId.value;
            company.accountManager = this.users.find(x => x.id === this.form.controls.accountManagerId.value) as unknown as User;
            company.partnerManager = this.users.find(x => x.id === this.form.controls.partnerManagerId.value) as unknown as User;
          }

          this.toastr.info('Partner and Account Manager details were updated', 'Success');
          this.activeModal.close();
          this.form.enable();
          this.isLoading = false;
        });
  }

  cancel(): void {
    this.activeModal.dismiss();
  }
}
